import { useState, useEffect, useCallback } from "react";
import type { BlogPublishOutputPlanItem } from "@ease/page-builder/blog";
import { apiFetch } from "@/lib/api-client";

const BLOG_PAGE_SIZE = 12;

export interface BlogSiteSummary {
  id: string;
  name: string;
  slug: string;
  status: "draft" | "published" | "archived";
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

export interface BlogTag {
  id: string;
  name: string;
  slug: string;
}

export interface BlogSeries {
  id: string;
  siteId: string;
  title: string;
  slug: string;
  description: string | null;
}

export interface BlogComment {
  id: string;
  postId: string;
  parentId: string | null;
  authorName: string;
  authorUserId: string | null;
  content: string;
  aiClassification: "spam" | "constructive" | "question" | "complaint" | "purchase_intent" | null;
  aiConfidence: number | null;
  aiRationale: string | null;
  status: "pending" | "approved" | "spam" | "deleted";
  likesCount: number;
  isPinned: boolean;
  createdAt: string;
  replies: BlogComment[];
}

export interface BlogCommentCollection {
  items: BlogComment[];
  total: number;
  blockCounts?: Record<string, number>;
}

export interface BlogSubscription {
  id: string;
  siteId: string;
  confirmed: boolean;
  categoryPreferences: string[];
  createdAt: string;
}

export interface BlogPost {
  id: string;
  siteId: string;
  pageId: string;
  slug: string;
  title: string;
  excerpt: string | null;
  featuredImage: string | null;
  authorId: string;
  authorName: string;
  status: "draft" | "published" | "scheduled" | "archived";
  publishedAt: string | null;
  scheduledFor: string | null;
  readingTimeMin: number | null;
  seoMeta: Record<string, unknown>;
  structuredData: Record<string, unknown>;
  seriesId: string | null;
  seriesOrder: number | null;
  canonicalUrl: string | null;
  sendToSubscribersOnPublish: boolean;
  newsletterSent: boolean;
  createSocialDraftOnPublish: boolean;
  socialDraftPostId: string | null;
  createdAt: string;
  updatedAt: string;
  content?: unknown;
  commentCount?: number;
  allowComments?: boolean;
  categories?: BlogCategory[];
  tags?: BlogTag[];
}

export interface BlogPublishResult extends BlogPost {
  publishOutputs: BlogPublishOutputPlanItem[];
  publishOutputCount: number;
  sitePublish: {
    status: "queued" | "skipped";
    jobId?: string;
    totalPages?: number;
    liveUrl?: string | null;
    error?: string;
    message?: string | null;
  };
}

export interface BlogPerformanceSummary {
  postId: string;
  pageId: string;
  slug: string;
  attributedLeads: number;
  attributedSubmissions: number;
}

interface BlogPostListEnvelope {
  items?: BlogPost[];
  total?: number;
}

function blogPath(siteId: string, suffix: string): string {
  return `/crm/pages/sites/${siteId}/blog${suffix}`;
}

export function useBlogSites() {
  const [sites, setSites] = useState<BlogSiteSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    void apiFetch<{ data: BlogSiteSummary[] }>("/crm/pages/sites")
      .then((response) => {
        if (!isCancelled) {
          setSites(response.data ?? []);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setSites([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  return { sites, isLoading };
}

export function useBlogPosts(
  siteId: string | undefined,
  filters?: {
    categoryId?: string;
    search?: string;
    status?: BlogPost["status"];
    page?: number;
  },
) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPosts = useCallback(async () => {
    if (!siteId) {
      setPosts([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("limit", String(BLOG_PAGE_SIZE));
      params.set("offset", String(((filters?.page ?? 1) - 1) * BLOG_PAGE_SIZE));

      if (filters?.categoryId) params.set("categoryId", filters.categoryId);
      if (filters?.search) params.set("search", filters.search);
      if (filters?.status) params.set("status", filters.status);

      const response = await apiFetch<{ data?: BlogPostListEnvelope }>(
        `${blogPath(siteId, "/posts")}?${params.toString()}`,
      );

      setPosts(response.data?.items ?? []);
      setTotal(response.data?.total ?? 0);
    } catch {
      setPosts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters?.categoryId, filters?.page, filters?.search, filters?.status, siteId]);

  useEffect(() => {
    void fetchPosts();
  }, [fetchPosts]);

  return { posts, total, isLoading, refetch: fetchPosts, pageSize: BLOG_PAGE_SIZE };
}

export function useBlogPost(siteIdOrPostId: string | undefined, postIdArg?: string | undefined) {
  const siteId = postIdArg === undefined ? undefined : siteIdOrPostId;
  const postId = postIdArg === undefined ? siteIdOrPostId : postIdArg;
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!siteId || !postId || postId === "new") {
      setPost(null);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    void apiFetch<{ data: BlogPost }>(blogPath(siteId, `/posts/${postId}`))
      .then((response) => {
        if (!isCancelled) {
          setPost(response.data);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPost(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [postId, siteId]);

  return { post, isLoading };
}

export function useBlogPerformance(siteId: string | undefined, postId: string | undefined) {
  const [performance, setPerformance] = useState<BlogPerformanceSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!siteId || !postId || postId === "new") {
      setPerformance(null);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    void apiFetch<{ data: BlogPerformanceSummary }>(blogPath(siteId, `/posts/${postId}/performance`))
      .then((response) => {
        if (!isCancelled) {
          setPerformance(response.data ?? null);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPerformance(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [postId, siteId]);

  return { performance, isLoading };
}

export function useBlogCategories(siteId: string | undefined) {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const fetchCategories = useCallback(async () => {
    if (!siteId) {
      setCategories([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiFetch<{ data: BlogCategory[] }>(blogPath(siteId, "/categories"));
      setCategories(response.data ?? []);
    } catch {
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    let isCancelled = false;

    void (async () => {
      if (!siteId) {
        setCategories([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const response = await apiFetch<{ data: BlogCategory[] }>(blogPath(siteId, "/categories"));
        if (!isCancelled) {
          setCategories(response.data ?? []);
        }
      } catch {
        if (!isCancelled) {
          setCategories([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [siteId]);

  return { categories, isLoading, refetch: fetchCategories };
}

export function useBlogSubscribers(siteId: string | undefined) {
  const [subscribers, setSubscribers] = useState<BlogSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSubscribers = useCallback(async () => {
    if (!siteId) {
      setSubscribers([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiFetch<{ data: BlogSubscription[] }>(blogPath(siteId, "/subscribers"));
      setSubscribers(response.data ?? []);
    } catch {
      setSubscribers([]);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void fetchSubscribers();
  }, [fetchSubscribers]);

  return { subscribers, isLoading, refetch: fetchSubscribers };
}

export function useBlogSeries(siteId: string | undefined) {
  const [series, setSeries] = useState<BlogSeries[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSeries = useCallback(async () => {
    if (!siteId) {
      setSeries([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiFetch<{ data: BlogSeries[] }>(blogPath(siteId, "/series"));
      setSeries(response.data ?? []);
    } catch {
      setSeries([]);
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void fetchSeries();
  }, [fetchSeries]);

  return { series, isLoading, refetch: fetchSeries };
}

export function useBlogSeriesPosts(siteId: string | undefined, seriesId: string | null | undefined) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!siteId || !seriesId) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    void apiFetch<{ data: BlogPost[] }>(blogPath(siteId, `/series/${seriesId}/posts`))
      .then((response) => {
        if (!isCancelled) {
          setPosts(response.data ?? []);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setPosts([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [seriesId, siteId]);

  return { posts, isLoading };
}

export async function createBlogSeries(
  siteId: string,
  data: { title: string; description?: string | null },
): Promise<BlogSeries> {
  const response = await apiFetch<{ data: BlogSeries }>(blogPath(siteId, "/series"), {
    method: "POST",
    body: JSON.stringify(data),
  });

  return response.data;
}

export async function createBlogCategory(siteId: string, name: string): Promise<BlogCategory> {
  const response = await apiFetch<{ data: BlogCategory }>(blogPath(siteId, "/categories"), {
    method: "POST",
    body: JSON.stringify({ name }),
  });

  return response.data;
}

export async function createBlogPost(
  siteId: string,
  data: {
    title: string;
    slug?: string;
    excerpt?: string;
    categoryIds?: string[];
    featuredImage?: string;
    authorName?: string;
  },
): Promise<BlogPost> {
  const response = await apiFetch<{ data: BlogPost }>(blogPath(siteId, "/posts"), {
    method: "POST",
    body: JSON.stringify(data),
  });

  return response.data;
}

export async function createBlogPostWithMetadata(
  siteId: string,
  data: {
    title: string;
    slug?: string;
    excerpt?: string;
    categoryIds?: string[];
    featuredImage?: string;
    authorName?: string;
    seoMeta?: Record<string, unknown>;
    canonicalUrl?: string | null;
    seriesId?: string | null;
    seriesOrder?: number | null;
    createSocialDraftOnPublish?: boolean;
  },
): Promise<BlogPost> {
  const created = await createBlogPost(siteId, {
    title: data.title,
    slug: data.slug,
    excerpt: data.excerpt,
    featuredImage: data.featuredImage,
    categoryIds: data.categoryIds,
  });

  return updateBlogPost(siteId, created.id, {
    title: data.title,
    slug: data.slug,
    excerpt: data.excerpt ?? null,
    featuredImage: data.featuredImage ?? null,
    authorName: data.authorName,
    categoryIds: data.categoryIds,
    seoMeta: data.seoMeta,
    canonicalUrl: data.canonicalUrl ?? null,
    seriesId: data.seriesId ?? null,
    seriesOrder: data.seriesOrder ?? null,
    createSocialDraftOnPublish: data.createSocialDraftOnPublish ?? true,
  });
}

export async function updateBlogPost(
  siteId: string,
  postId: string,
  data: Partial<{
    title: string;
    slug: string;
    excerpt: string | null;
    categoryIds: string[];
    featuredImage: string | null;
    authorName: string;
    seoMeta: Record<string, unknown>;
    canonicalUrl: string | null;
    sendToSubscribersOnPublish: boolean;
    createSocialDraftOnPublish: boolean;
    seriesId: string | null;
    seriesOrder: number | null;
  }>,
): Promise<BlogPost> {
  const response = await apiFetch<{ data: BlogPost }>(blogPath(siteId, `/posts/${postId}`), {
    method: "PUT",
    body: JSON.stringify(data),
  });

  return response.data;
}

export async function publishBlogPost(siteId: string, postId: string): Promise<BlogPublishResult> {
  const response = await apiFetch<{ data: BlogPublishResult }>(blogPath(siteId, `/posts/${postId}/publish`), {
    method: "POST",
  });

  return response.data;
}

export async function scheduleBlogPost(
  siteId: string,
  postId: string,
  scheduledFor: string,
): Promise<BlogPost> {
  const response = await apiFetch<{ data: BlogPost }>(blogPath(siteId, `/posts/${postId}/schedule`), {
    method: "POST",
    body: JSON.stringify({ scheduledFor }),
  });

  return response.data;
}

export async function deleteBlogPost(siteId: string, postId: string): Promise<void> {
  await apiFetch(blogPath(siteId, `/posts/${postId}`), {
    method: "DELETE",
  });
}

export function useBlogComments(
  siteId: string | undefined,
  postId: string | undefined,
  filters?: {
    status?: BlogComment["status"];
    blockId?: string;
    includeBlockCounts?: boolean;
    page?: number;
  },
) {
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [total, setTotal] = useState(0);
  const [blockCounts, setBlockCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!siteId || !postId) {
      setComments([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("limit", String(BLOG_PAGE_SIZE));
      params.set("offset", String(((filters?.page ?? 1) - 1) * BLOG_PAGE_SIZE));

      if (filters?.status) params.set("status", filters.status);
      if (filters?.blockId) params.set("blockId", filters.blockId);
      if (filters?.includeBlockCounts) params.set("includeBlockCounts", "true");

      const response = await apiFetch<{ data?: BlogCommentCollection }>(
        `${blogPath(siteId, `/posts/${postId}/comments`)}?${params.toString()}`,
      );

      setComments(response.data?.items ?? []);
      setTotal(response.data?.total ?? 0);
      setBlockCounts(response.data?.blockCounts ?? {});
    } catch {
      setComments([]);
      setTotal(0);
      setBlockCounts({});
    } finally {
      setIsLoading(false);
    }
  }, [filters?.blockId, filters?.includeBlockCounts, filters?.page, filters?.status, postId, siteId]);

  useEffect(() => {
    void fetchComments();
  }, [fetchComments]);

  return { comments, total, blockCounts, isLoading, refetch: fetchComments, pageSize: BLOG_PAGE_SIZE };
}

export async function createBlogComment(
  siteId: string,
  postId: string,
  data: { content: string; authorName: string; blockId?: string; parentId?: string },
): Promise<BlogComment> {
  const response = await apiFetch<{ data: BlogComment }>(
    blogPath(siteId, `/posts/${postId}/comments`),
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );

  return response.data;
}

export async function moderateComment(
  siteId: string,
  postId: string,
  commentId: string,
  action: "approve" | "spam" | "delete" | "pin",
): Promise<BlogComment> {
  const response = await apiFetch<{ data: BlogComment }>(
    blogPath(siteId, `/posts/${postId}/comments/${commentId}/moderate`),
    {
      method: "POST",
      body: JSON.stringify({ action }),
    },
  );

  return response.data;
}

export async function likeComment(
  siteId: string,
  postId: string,
  commentId: string,
): Promise<BlogComment> {
  const response = await apiFetch<{ data: BlogComment }>(
    blogPath(siteId, `/posts/${postId}/comments/${commentId}/like`),
    {
      method: "POST",
    },
  );

  return response.data;
}

export async function subscribeToBlog(
  siteId: string,
  data: { encryptedEmail: string; emailHash: string; categoryPreferences?: string[] },
): Promise<BlogSubscription> {
  const response = await apiFetch<{ data: BlogSubscription }>(
    blogPath(siteId, "/subscribe"),
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );

  return response.data;
}

export async function confirmSubscription(
  siteId: string,
  token: string,
): Promise<BlogSubscription> {
  const response = await apiFetch<{ data: BlogSubscription }>(
    blogPath(siteId, "/subscribe/confirm"),
    {
      method: "POST",
      body: JSON.stringify({ token }),
    },
  );

  return response.data;
}

export async function unsubscribeFromBlog(
  siteId: string,
  token: string,
): Promise<BlogSubscription> {
  const response = await apiFetch<{ data: BlogSubscription }>(
    blogPath(siteId, "/subscribe/unsubscribe"),
    {
      method: "POST",
      body: JSON.stringify({ token }),
    },
  );

  return response.data;
}
